import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { surveyService, providerService } from '../../../services/surveyService';
import api from '../../../services/api';
import {
  Button, StatusBadge, Card, Modal, Spinner,
  EmptyState, PageHeader, Input, Select, PeriodBadges
} from '../../../shared/sharedComponent';
import { toast } from 'react-toastify';
import '../SurveyList/SurveyList.css';

const STATUS_OPTIONS = ['draft'];
const TYPE_OPTIONS   = ['', 'monthly', 'quarterly'];
const SECTOR_OPTIONS = ['', 'manufacturing', 'wholesale_retail'];
const FORM_OPTIONS   = ['', 'MQ1', 'WRTQ1', 'MM1', 'Monthly_WRT', 'GQ1'];
const FORM_LABELS = {
  MQ1: 'MQ1 — Manufacturing Quarterly',
  WRTQ1: 'WRTQ1 — Wholesale & Retail Quarterly',
  MM1: 'MM1 — Manufacturing Monthly',
  Monthly_WRT: 'Monthly WRT — Wholesale & Retail Monthly',
  GQ1: 'GQ1 — Generic Questionnaire',
};

const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff   = Date.now() - new Date(dateStr).getTime();
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(diff / 3600000);
  const days   = Math.floor(diff / 86400000);
  const weeks  = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  if (weeks < 5)  return `${weeks}w ago`;
  return `${months}mo ago`;
};

const statusTime = (s) => {
  if (s.survey_status === 'submitted') return timeAgo(s.survey_date_submitted);
  if (s.survey_status === 'closed')    return timeAgo(s.survey_close_date);
  if (s.survey_status === 'draft')     return timeAgo(s.created_at);
  return timeAgo(s.survey_close_date);
};

const DraftSurveys = () => {
  const navigate = useNavigate();

  const [surveys, setSurveys]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filters, setFilters]     = useState({ status: '', survey_type: '', sector: '', form_code: '', date_from: '', date_to: '' });

  // Dispatch log modal
  const [logModal, setLogModal]     = useState(false);
  const [logSurvey, setLogSurvey]   = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Dispatch modal
  const [dispatchModal, setDispatchModal]   = useState(false);
  const [dispatchSurvey, setDispatchSurvey] = useState(null);
  const [providers, setProviders]           = useState([]);
  const [allProviders, setAllProviders]     = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [dispatchEstFilter, setDispatchEstFilter] = useState('');
  const [dispatching, setDispatching]       = useState(false);

  // Batch dispatch (non-universal surveys only) — selection persists across
  // pages, so we also cache the full survey object for each selected id
  // (keyed by survey_id) since `surveys` only ever holds the current page's rows
  const [selectedSurveys, setSelectedSurveys]   = useState([]);
  const [selectedSurveyMap, setSelectedSurveyMap] = useState({});
  const [bulkDispatchModal, setBulkDispatchModal] = useState(false);
  const [bulkDispatching, setBulkDispatching]   = useState(false);
  const [bulkDispatchResults, setBulkDispatchResults] = useState(null);

  const setFilter = (key, val) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
    setPage(1);
  };

  const clearDates = () => setFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await surveyService.getAll({ ...filters, page, limit: 15, view: 'draft' });
      setSurveys(data.data || []);
      setPagination({
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 1,
      });
    } catch { toast.error('Failed to load surveys.'); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { loadSurveys(); }, [loadSurveys]);
  // Only reset the selection when filters change (the eligible survey set
  // itself changed) — NOT on page change, so selections made on one page
  // survive navigating to another page.
  useEffect(() => { setSelectedSurveys([]); setSelectedSurveyMap({}); }, [filters]);

  // ── Batch dispatch (non-universal drafts only — universal surveys'
  // sector-wide reach makes them unsuitable for a bulk "select and blast" action) ──
  const eligibleForBatch = surveys.filter((s) => !s.is_universal).map((s) => s.survey_id);
  const selectedOnPage = eligibleForBatch.filter((id) => selectedSurveys.includes(id));

  const toggleSurveySelect = (survey) => {
    const surveyId = survey.survey_id;
    setSelectedSurveys((prev) =>
      prev.includes(surveyId) ? prev.filter((id) => id !== surveyId) : [...prev, surveyId]
    );
    setSelectedSurveyMap((prev) => {
      if (prev[surveyId]) {
        const next = { ...prev };
        delete next[surveyId];
        return next;
      }
      return { ...prev, [surveyId]: survey };
    });
  };

  const toggleSelectAll = () => {
    const allOnPageSelected = eligibleForBatch.length > 0 && selectedOnPage.length === eligibleForBatch.length;
    if (allOnPageSelected) {
      // Deselect only this page's surveys, leaving other pages' selections intact
      setSelectedSurveys((prev) => prev.filter((id) => !eligibleForBatch.includes(id)));
      setSelectedSurveyMap((prev) => {
        const next = { ...prev };
        eligibleForBatch.forEach((id) => delete next[id]);
        return next;
      });
    } else {
      // Add this page's eligible surveys to whatever's already selected elsewhere
      setSelectedSurveys((prev) => [...new Set([...prev, ...eligibleForBatch])]);
      setSelectedSurveyMap((prev) => {
        const next = { ...prev };
        surveys.filter((s) => !s.is_universal).forEach((s) => { next[s.survey_id] = s; });
        return next;
      });
    }
  };

  const handleBulkDispatch = async () => {
    if (!selectedSurveys.length) return;
    setBulkDispatching(true);
    try {
      const { data } = await api.post('/surveys/bulk-dispatch', { survey_ids: selectedSurveys });
      const results = data.data || [];
      setBulkDispatchResults(results);
      const failed = results.filter((r) => !r.success);
      if (failed.length) toast.warn(data.message);
      else toast.success(data.message);
      setSelectedSurveys([]);
      setSelectedSurveyMap({});
      loadSurveys();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Batch dispatch failed.');
    } finally {
      setBulkDispatching(false);
    }
  };

  const closeBulkDispatchModal = () => {
    setBulkDispatchModal(false);
    setBulkDispatchResults(null);
  };

  const openDispatch = async (survey) => {
    setDispatchSurvey(survey);
    setSelectedProviders([]);
    setDispatchEstFilter('');
    setDispatchModal(true);
    try {
      // Universal surveys target the whole sector; GQ1 surveys spanning
      // several specific establishments target that whole set; everything
      // else targets one establishment — scope the provider list
      // accordingly so we don't show every active provider system-wide.
      const params = survey.is_universal
        ? { status: 'active', sector: survey.survey_sector_type }
        : survey.establishment_ids?.length
          ? { status: 'active', establishment_ids: survey.establishment_ids.join(',') }
          : { status: 'active', establishment_id: survey.establishment_id };
      const { data } = await providerService.getAll(params);
      const already = (survey.dispatches || []).map((d) => d.dp_id);
      const undispatched = data.data.filter((p) => !already.includes(p.dp_id));
      setAllProviders(undispatched);
      setProviders(undispatched);
    } catch { toast.error('Failed to load providers.'); }
  };

  const handleDispatch = async () => {
    if (!selectedProviders.length) return;
    setDispatching(true);
    try {
      await surveyService.dispatch(dispatchSurvey.survey_id, selectedProviders);
      toast.success(`Dispatched to ${selectedProviders.length} provider(s).`);
      setDispatchModal(false);
      loadSurveys();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Dispatch failed.');
    } finally { setDispatching(false); }
  };

  const openDispatchLog = async (survey) => {
    setLogSurvey(survey);
    setLogModal(true);
    setLogLoading(true);
    try {
      const { data } = await api.get(`/surveys/${survey.survey_id}/dispatch-log`);
      setLogEntries(data.data || []);
    } catch { toast.error('Failed to load dispatch log.'); }
    finally { setLogLoading(false); }
  };

  return (
    <div className="surveys-list">
      <PageHeader
        title="Draft Surveys"
        subtitle="Surveys saved as draft and not yet dispatched"
        actions={
          <Button variant="secondary" onClick={() => navigate('/internal/surveys')}>
            ← Surveys
          </Button>
        }
      />

      {/* ── Filters ── */}
      <Card className="surveys-list__filters">
        <div className="filters-row">
          <div className="surveys-filter__dates">
            <label className="surveys-filter__dates-label">Date</label>
            <div className="surveys-filter__dates-row">
              <span className="surveys-filter__dates-field">
                <span className="surveys-filter__dates-prefix">From</span>
                <input type="date" value={filters.date_from} max={filters.date_to || undefined}
                  onChange={(e) => setFilter('date_from', e.target.value)} />
              </span>
              <span className="surveys-filter__dates-sep">|</span>
              <span className="surveys-filter__dates-field">
                <span className="surveys-filter__dates-prefix">To</span>
                <input type="date" value={filters.date_to} min={filters.date_from || undefined}
                  onChange={(e) => setFilter('date_to', e.target.value)} />
              </span>
              {(filters.date_from || filters.date_to) && (
                <button className="surveys-filter__clear-dates" onClick={clearDates}>✕</button>
              )}
            </div>
          </div>
          <Select className="surveys-filter__select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)} label="Status">
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? s.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'All statuses'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.survey_type} onChange={(e) => setFilter('survey_type', e.target.value)} label="Type">
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t ? t.charAt(0).toUpperCase() + t.slice(1) : 'All types'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.sector} onChange={(e) => setFilter('sector', e.target.value)} label="Industry">
            {SECTOR_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? s.replace('_', ' & ').replace(/^\w/, (c) => c.toUpperCase()) : 'All Industries'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.form_code} onChange={(e) => setFilter('form_code', e.target.value)} label="Form">
            {FORM_OPTIONS.map((f) => (
              <option key={f} value={f}>{f ? FORM_LABELS[f] : 'All forms'}</option>
            ))}
          </Select>
        </div>
      </Card>

      {/* ── Table ── */}
      <Card padding={false}>
        <div className="surveys-table">
          {/* Batch dispatch toolbar */}
          {selectedSurveys.length > 0 && (
            <div className="surveys-bulk-toolbar">
              <span className="surveys-bulk-toolbar__count">{selectedSurveys.length} survey{selectedSurveys.length !== 1 ? 's' : ''} selected</span>
              <button className="surveys-bulk-toolbar__btn" onClick={() => { setBulkDispatchResults(null); setBulkDispatchModal(true); }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                Dispatch Selected
              </button>
              <button className="surveys-bulk-toolbar__clear" onClick={() => { setSelectedSurveys([]); setSelectedSurveyMap({}); }}>✕ Clear</button>
            </div>
          )}

          <div className="surveys-table__head">
            <span className="surveys-table__head--check">
              {eligibleForBatch.length > 0 && (
                <input type="checkbox"
                  ref={(el) => {
                    if (el) el.indeterminate = selectedOnPage.length > 0 && selectedOnPage.length < eligibleForBatch.length;
                  }}
                  checked={selectedOnPage.length > 0 && selectedOnPage.length === eligibleForBatch.length}
                  onChange={toggleSelectAll}
                />
              )}
            </span>
            <span>Survey</span>
            <span>Data Sources</span>
            <span>Period</span>
            <span>Dates</span>
            <span>Status / Dispatched</span>
            <span>Actions</span>
          </div>

          {loading ? (
            <div className="surveys-list__loading"><Spinner size="lg" /></div>
          ) : surveys.length === 0 ? (
            <EmptyState
              title="No draft surveys"
              description="Surveys saved as drafts will appear here. Dispatch them to send to data providers."
            />
          ) : surveys.map((s) => (
            <div key={s.survey_id} className="surveys-table__row">
              <div className="surveys-table__col surveys-table__col--check">
                <input type="checkbox"
                  checked={!s.is_universal && selectedSurveys.includes(s.survey_id)}
                  disabled={s.is_universal}
                  title={s.is_universal ? 'Universal surveys must be dispatched individually' : undefined}
                  onChange={() => toggleSurveySelect(s)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Survey */}
              <div className="surveys-table__col">
                <span className="surveys-table__code">{s.survey_form_code}</span>
                {s.is_universal && (
                  <span className="surveys-table__universal-badge">🌐 Universal</span>
                )}
                <span className="surveys-table__title">{s.survey_title}</span>
                <span className="surveys-table__creator">by {s.created_by_name}</span>
              </div>

              {/* Establishment */}
              <div className="surveys-table__col">
                {s.is_universal
                  ? <span className="surveys-table__all-badge">All</span>
                  : <span className="surveys-table__est">{s.establishment_name || '—'}</span>
                }
              </div>

              {/* Period */}
              <div className="surveys-table__col">
                <PeriodBadges periods={s.period_names?.length ? s.period_names : [s.quarter_name || s.month_name]} />
              </div>

              {/* Dates */}
              <div className="surveys-table__col surveys-table__col--dates">
                <span><strong>Open:</strong> {new Date(s.survey_open_date).toLocaleDateString('en-NA')}</span>
                <span><strong>Close:</strong> {new Date(s.survey_close_date).toLocaleDateString('en-NA')}</span>
              </div>

              {/* Status pill */}
              <div className="surveys-table__col surveys-table__col--meta">
                <div className="surveys-table__meta-pill">
                  <StatusBadge status={s.survey_status} />
                  <span className="surveys-table__meta-divider" />
                  {statusTime(s) && (
                    <span className="surveys-table__meta-ago">{statusTime(s)}</span>
                  )}
                  {statusTime(s) && <span className="surveys-table__meta-divider" />}
                  <span className="surveys-table__meta-count">{s.dispatched_count ?? 0}</span>
                </div>
              </div>

              <div className="surveys-table__col surveys-table__col--actions">
                <div className="sl-actions">
                  <button className="sl-action sl-action--view"
                    onClick={() => navigate(`/internal/surveys/${s.survey_id}`)}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                    View
                  </button>
                  <button className="sl-action sl-action--emails"
                    onClick={() => navigate(`/internal/surveys/${s.survey_id}?edit=true`)}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                    Edit
                  </button>
                  <button className="sl-action sl-action--dispatch"
                    onClick={() => openDispatch(s)}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                    Dispatch
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="surveys-list__pagination">
            <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
            <span>Page {page} of {pagination.pages}</span>
            <Button variant="secondary" size="sm" disabled={page === pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
          </div>
        )}
      </Card>

      {/* ── Dispatch Modal ── */}
      <Modal open={dispatchModal} onClose={() => setDispatchModal(false)}
        title={`Dispatch — ${dispatchSurvey?.survey_form_code}`} size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDispatchModal(false)}>Cancel</Button>
            <Button variant="primary" loading={dispatching}
              disabled={!selectedProviders.length} onClick={handleDispatch}>
              Send to {selectedProviders.length} Provider{selectedProviders.length !== 1 ? 's' : ''}
            </Button>
          </>
        }
      >
        <div className="dispatch-modal">
          <div className="dispatch-modal__survey-info">
            <p><strong>{dispatchSurvey?.survey_title}</strong></p>
            {dispatchSurvey?.is_universal
              ? <span className="dispatch-modal__universal-badge">🌐 Universal — {dispatchSurvey?.survey_sector_type === 'manufacturing' ? 'Manufacturing' : 'Wholesale & Retail'} Industries</span>
              : <p>{dispatchSurvey?.establishment_name}</p>
            }
          </div>

          <div className="dispatch-modal__filters">
            {dispatchSurvey?.is_universal && (
              <select className="dispatch-modal__est-filter" value={dispatchEstFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setDispatchEstFilter(val);
                  setProviders(val
                    ? allProviders.filter((p) =>
                        (p.establishments || []).some((est) => est.establishment_id === val) ||
                        p.establishment_id === val
                      )
                    : allProviders
                  );
                }}>
                <option value="">All Data Sources</option>
                {[...new Map(
                  allProviders.flatMap((p) =>
                    (p.establishments && p.establishments.length > 0)
                      ? p.establishments
                      : p.establishment_name
                        ? [{ establishment_id: p.establishment_id, establishment_name: p.establishment_name }]
                        : []
                  ).map((e) => [e.establishment_id, e])
                ).values()].map((e) => (
                  <option key={e.establishment_id} value={e.establishment_id}>{e.establishment_name}</option>
                ))}
              </select>
            )}
            {providers.length > 0 && (
              <label className="dispatch-modal__select-all">
                <input type="checkbox"
                  checked={selectedProviders.length === providers.length && providers.length > 0}
                  onChange={() => setSelectedProviders(
                    selectedProviders.length === providers.length ? [] : providers.map((p) => p.dp_id)
                  )}
                />
                Select all ({providers.length})
              </label>
            )}
          </div>

          <p className="dispatch-modal__label">Select providers to notify:</p>
          {providers.length === 0 ? (
            <div className="dispatch-modal__empty">No available providers for this survey.</div>
          ) : (
            <div className="dispatch-modal__list">
              {providers.map((p) => (
                <label key={p.dp_id}
                  className={`dispatch-provider ${selectedProviders.includes(p.dp_id) ? 'dispatch-provider--selected' : ''}`}>
                  <input type="checkbox" checked={selectedProviders.includes(p.dp_id)}
                    onChange={() => setSelectedProviders((prev) =>
                      prev.includes(p.dp_id) ? prev.filter((x) => x !== p.dp_id) : [...prev, p.dp_id]
                    )}
                  />
                  <div className="dispatch-provider__avatar">{p.dp_firstname?.[0]}{p.dp_lastname?.[0]}</div>
                  <div className="dispatch-provider__info">
                    <span className="dispatch-provider__name">{p.dp_firstname} {p.dp_lastname}</span>
                    <span className="dispatch-provider__email">{p.dp_email}</span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Batch Dispatch Confirmation / Results Modal ── */}
      <Modal open={bulkDispatchModal} onClose={closeBulkDispatchModal}
        title={bulkDispatchResults
          ? `Dispatch Results — ${bulkDispatchResults.filter((r) => r.success).length} of ${bulkDispatchResults.length} sent`
          : `Dispatch ${selectedSurveys.length} Survey${selectedSurveys.length !== 1 ? 's' : ''}`}
        size="md"
        footer={
          bulkDispatchResults ? (
            <Button variant="primary" onClick={closeBulkDispatchModal}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeBulkDispatchModal}>Cancel</Button>
              <Button variant="primary" loading={bulkDispatching} onClick={handleBulkDispatch}>
                Confirm & Dispatch
              </Button>
            </>
          )
        }
      >
        <div className="dispatch-modal">
          {bulkDispatchResults ? (
            <div className="dispatch-modal__list">
              {bulkDispatchResults.map((r) => (
                <div key={r.survey_id} className="dispatch-provider">
                  <div className={`dispatch-provider__avatar ${r.success ? '' : 'dispatch-provider__avatar--failed'}`}>
                    {r.success ? '✓' : '✕'}
                  </div>
                  <div className="dispatch-provider__info">
                    <span className="dispatch-provider__name">{r.survey_title || r.survey_id}</span>
                    <span className="dispatch-provider__email">
                      {r.success ? `Sent to ${r.dispatchedCount} provider(s)` : r.message}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="dispatch-modal__label">
                Each survey below will be sent to all active data providers for its own establishment(s).
                Emails are sent one at a time, so this may take a moment for larger batches — please
                don't close this window while it's in progress.
              </p>
              <div className="dispatch-modal__list">
                {selectedSurveys.map((id) => selectedSurveyMap[id]).filter(Boolean).map((s) => (
                  <div key={s.survey_id} className="dispatch-provider">
                    <div className="dispatch-provider__avatar">{s.survey_form_code?.slice(0, 2)}</div>
                    <div className="dispatch-provider__info">
                      <span className="dispatch-provider__name">{s.survey_title}</span>
                      <span className="dispatch-provider__email">{s.survey_form_code} · {s.establishment_name || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ── Dispatch Log Modal ── */}
      <Modal
        open={logModal}
        onClose={() => setLogModal(false)}
        title={`Email Dispatch Log — ${logSurvey?.survey_form_code}`}
        size="md"
      >
        <div className="dispatch-log">
          <p className="dispatch-log__subtitle">{logSurvey?.survey_title}</p>
          {logLoading ? (
            <div style={{ textAlign: 'center', padding: '32px' }}><Spinner /></div>
          ) : logEntries.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '24px', color: 'var(--gray-400)' }}>
              No emails dispatched for this survey.
            </p>
          ) : (
            <div className="dispatch-log__list">
              <div className="dispatch-log__stats">
                <div className="dispatch-log__stat dispatch-log__stat--sent">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <div>
                    <span className="dispatch-log__stat-num">{logEntries.filter(e => e.email_sent).length}</span>
                    <span className="dispatch-log__stat-label">Sent</span>
                  </div>
                </div>
                <div className="dispatch-log__stat dispatch-log__stat--failed">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9a1 1 0 012 0v4a1 1 0 01-2 0V9zm1 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                  <div>
                    <span className="dispatch-log__stat-num">{logEntries.filter(e => !e.email_sent).length}</span>
                    <span className="dispatch-log__stat-label">Failed</span>
                  </div>
                </div>
              </div>
              {logEntries.map((entry) => (
                <div key={entry.dispatch_id} className="dispatch-log__row">
                  <div className="dispatch-log__row-left">
                    <div className={`dispatch-log__avatar ${!entry.email_sent ? 'dispatch-log__avatar--failed' : ''}`}>
                      {entry.dp_name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div className="dispatch-log__info">
                      <span className="dispatch-log__name">{entry.dp_name}</span>
                      <span className="dispatch-log__email">{entry.dp_email}</span>
                      <span className="dispatch-log__date">Sent: {new Date(entry.dispatched_at).toLocaleString('en-NA')}</span>
                    </div>
                  </div>
                  {entry.email_sent ? (
                    <span className="dispatch-log__badge dispatch-log__badge--sent">
                      <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M13.707 4.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6 10.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      Email sent
                    </span>
                  ) : (
                    <span className="dispatch-log__badge dispatch-log__badge--failed">
                      <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 5a1 1 0 012 0v3a1 1 0 01-2 0V5zm1 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                      Failed
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DraftSurveys;